import React, { useState, useEffect } from 'react';
import {
    Eye, Heart, HeartOff, MessageSquare, Phone, Mail, Bell,
    TrendingUp, Calendar, Loader2, AlertCircle, Image,
    ChevronDown, ChevronUp, User, ExternalLink, DollarSign,
    Landmark, Coins
} from 'lucide-react';
import { fetchEventDetails } from '../../features/analytics/api/analyticsApi';
import { useNavigate } from 'react-router-dom';
import { syncVirtualContact } from '../../api/contact';
import toast from 'react-hot-toast';
import BuyerActionsModal from './BuyerActionsModal';
import { formatMelbourneDateTime } from '../../utils/melbourneTime';
import './PropertyAnalyticsTimeline.css';

// ─── Event config ────────────────────────────────────────────────────────────
const EVENT_CONFIG = {
    property_viewed:           { icon: Eye,            label: 'Property Viewed',         color: 'blue'   },
    property_gallery_viewed:   { icon: Image,          label: 'Gallery Browsed',          color: 'indigo' },
    property_saved:            { icon: Heart,          label: 'Property Saved',           color: 'pink'   },
    property_unsaved:          { icon: HeartOff,       label: 'Property Unsaved',         color: 'gray'   },
    enquiry_sent:              { icon: MessageSquare,  label: 'Enquiry Sent',             color: 'emerald'},
    offer_submitted:           { icon: DollarSign,     label: 'Offer Submitted',          color: 'violet' },
    call_clicked:              { icon: Phone,          label: 'Tapped Agent Phone',       color: 'green'  },
    email_clicked:             { icon: Mail,           label: 'Tapped Agent Email',       color: 'teal'   },
    inspection_requested:      { icon: Calendar,       label: 'Requested Inspection',     color: 'orange' },
    signup_completed:          { icon: TrendingUp,     label: 'Buyer Signed Up',          color: 'purple' },
    alert_created:             { icon: Bell,           label: 'Property Alert Set',       color: 'yellow' },
    mortgage_calculator_opened:        { icon: Landmark, label: 'Opened Mortgage Calculator', color: 'teal' },
    mortgage_calculator_calculated:    { icon: Landmark, label: 'Mortgage Calculated',         color: 'teal' },
    mortgage_calculator_broker_clicked:{ icon: Coins,    label: 'Tapped Broker CTA',           color: 'teal' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatRelativeTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now  = new Date();
    const diffMs   = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1)  return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs  = Math.floor(diffMins / 60);
    if (diffHrs < 24)  return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7)  return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Stats bar ───────────────────────────────────────────────────────────────
function StatsBar({ events, onStatClick }) {
    // Identity key matching the modal's grouping logic (id → email → anon → unknown)
    const buyerKey = (e) => e.buyer?.id
        ? `buyer_${e.buyer.id}`
        : e.buyer?.email
            ? `email_${e.buyer.email}`
            : e.anonymous_id
                ? `anon_${e.anonymous_id}`
                : 'unknown';

    // Count unique buyers
    const uniqueBuyerIds = new Set(events.filter(e => e.buyer?.id).map(e => e.buyer.id));
    // Count unique buyers who saved (one buyer saving twice still counts as one)
    const uniqueSavers = new Set(
        events.filter(e => e.event_name === 'property_saved').map(buyerKey)
    );
    const counts = {
        total:     events.length,
        views:     events.filter(e => e.event_name === 'property_viewed').length,
        gallery:   events.filter(e => e.event_name === 'property_gallery_viewed').length,
        enquiries: events.filter(e => e.event_name === 'enquiry_sent').length,
        offers:    events.filter(e => e.event_name === 'offer_submitted').length,
        saved:     uniqueSavers.size,
        clicks:    events.filter(e => ['call_clicked','email_clicked'].includes(e.event_name)).length,
        buyers:    uniqueBuyerIds.size,
    };
    const items = [
        { label: 'Total Events',    value: counts.total,     key: 'total',     eventTypes: null },
        { label: 'Unique Buyers',   value: counts.buyers,    key: 'buyers',    eventTypes: null },
        { label: 'Views',           value: counts.views,     key: 'views',     eventTypes: ['property_viewed'] },
        { label: 'Gallery Views',   value: counts.gallery,   key: 'gallery',   eventTypes: ['property_gallery_viewed'] },
        { label: 'Enquiries',       value: counts.enquiries, key: 'enquiries', eventTypes: ['enquiry_sent'] },
        { label: 'Offers',          value: counts.offers,    key: 'offers',    eventTypes: ['offer_submitted'] },
        { label: 'Saves',           value: counts.saved,     key: 'saves',     eventTypes: ['property_saved'] },
        { label: 'Intent Clicks',   value: counts.clicks,    key: 'clicks',    eventTypes: ['call_clicked', 'email_clicked'] },
    ];
    return (
        <div className="pat-stats-bar">
            {items.map(({ label, value, key, eventTypes }) => (
                <button
                    key={key}
                    className="pat-stat pat-stat--clickable"
                    onClick={() => value > 0 && onStatClick && onStatClick(key, eventTypes, label)}
                    disabled={value === 0}
                    title={value > 0 ? `Click to see ${label.toLowerCase()} breakdown by buyer` : ''}
                >
                    <span className="pat-stat-value">{value}</span>
                    <span className="pat-stat-label">{label}</span>
                </button>
            ))}
        </div>
    );
}

// ─── Stat detail modal - shows breakdown by buyer ─────────────────────────────
function StatDetailModal({ isOpen, onClose, title, events, eventTypes, onBuyerClick }) {
    if (!isOpen) return null;

    // Filter events by type if specified
    const filteredEvents = eventTypes
        ? events.filter(e => eventTypes.includes(e.event_name))
        : events;

    // Group by buyer (use email as key for non-authenticated, id for authenticated)
    const buyerStats = {};
    filteredEvents.forEach(event => {
        const buyer = event.buyer;
        const key = buyer?.id
            ? `buyer_${buyer.id}`
            : buyer?.email
                ? `email_${buyer.email}`
                : event.anonymous_id
                    ? `anon_${event.anonymous_id}`
                    : 'unknown';

        if (!buyerStats[key]) {
            buyerStats[key] = {
                buyer: buyer || { name: 'Anonymous', email: null, is_guest: true },
                anonymous_id: event.anonymous_id,
                count: 0,
                events: [],
                lastActivity: null,
            };
        }
        buyerStats[key].count += 1;
        buyerStats[key].events.push(event);
        const eventTime = new Date(event.occurred_at);
        if (!buyerStats[key].lastActivity || eventTime > buyerStats[key].lastActivity) {
            buyerStats[key].lastActivity = eventTime;
        }
    });

    // Sort by count (descending), then by last activity (newest first)
    const sortedBuyers = Object.values(buyerStats).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return b.lastActivity - a.lastActivity;
    });

    return (
        <div className="pat-modal-overlay" onClick={onClose}>
            <div className="pat-modal" onClick={e => e.stopPropagation()}>
                <div className="pat-modal-header">
                    <h3>{title} - Breakdown by Buyer</h3>
                    <button className="pat-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pat-modal-body">
                    {sortedBuyers.length === 0 ? (
                        <p className="pat-modal-empty">No activity to show.</p>
                    ) : (
                        <div className="pat-buyer-ranking">
                            {sortedBuyers.map((item, index) => {
                                const { buyer, anonymous_id, count, lastActivity } = item;
                                const isGuest = buyer?.is_guest || !buyer?.id;
                                const name = buyer?.name || buyer?.email || (anonymous_id ? `Anonymous ${anonymous_id.slice(0, 8)}` : 'Unknown');
                                const initial = name.charAt(0).toUpperCase();

                                return (
                                    <div
                                        key={index}
                                        className={`pat-ranking-item ${!isGuest ? 'pat-ranking-item--clickable' : ''}`}
                                        onClick={() => !isGuest && onBuyerClick && onBuyerClick(buyer)}
                                    >
                                        <span className="pat-ranking-position">#{index + 1}</span>
                                        <span className={`pat-ranking-avatar ${isGuest ? 'pat-ranking-avatar--guest' : ''}`}>
                                            {initial}
                                        </span>
                                        <div className="pat-ranking-info">
                                            <span className="pat-ranking-name">{name}</span>
                                            {buyer?.email && buyer?.name && (
                                                <span className="pat-ranking-email">{buyer.email}</span>
                                            )}
                                            <span className="pat-ranking-last">
                                                Last: {formatRelativeTime(lastActivity?.toISOString())}
                                            </span>
                                        </div>
                                        <div className="pat-ranking-count">
                                            <span className="pat-ranking-count-value">{count}</span>
                                            <span className="pat-ranking-count-label">{count === 1 ? 'time' : 'times'}</span>
                                        </div>
                                        {!isGuest && <ExternalLink size={14} className="pat-ranking-link" />}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Buyer pill (clickable) ───────────────────────────────────────────────────
function BuyerPill({ buyer, onBuyerClick }) {
    if (!buyer || (!buyer.email && !buyer.name)) return null;
    const name = buyer.name || buyer.email;
    const initial = name.charAt(0).toUpperCase();
    const isGuest    = buyer.is_guest || !buyer.id;   // no portal account
    const isStitched = buyer.stitched === true;         // identity resolved via session

    const pillClass = isGuest
        ? 'pat-buyer-pill pat-buyer-pill--guest'
        : isStitched
            ? 'pat-buyer-pill pat-buyer-pill--stitched'
            : 'pat-buyer-pill';

    return (
        <button
            className={pillClass}
            onClick={() => !isGuest && onBuyerClick && onBuyerClick(buyer)}
            title={
                isGuest
                    ? `${name} (guest — no portal account)`
                    : isStitched
                        ? `${name} — identity resolved from same session`
                        : `View ${name}'s profile`
            }
            style={(isGuest || isStitched) ? { cursor: 'default' } : {}}
        >
            <span className={`pat-buyer-avatar ${isGuest ? 'pat-buyer-avatar--guest' : ''}`}>{initial}</span>
            <span className="pat-buyer-name">{name}</span>
            {buyer.email && buyer.name && (
                <span className="pat-buyer-email">{buyer.email}</span>
            )}
            {buyer.phone && (
                <span className="pat-buyer-phone">· {buyer.phone}</span>
            )}
            {isGuest
                ? <span className="pat-guest-badge">Guest</span>
                : isStitched
                    ? <span className="pat-stitched-badge">Viewed</span>
                    : <ExternalLink size={10} className="pat-buyer-link-icon" />
            }
        </button>
    );
}

// Enquiry type labels — same source as ContactActivityTimeline
const ENQUIRY_TYPE_LABELS = {
    GENERAL_ENQUIRY:    'General Enquiry',
    INSPECTION_REQUEST: 'Inspection Request',
    DOCUMENT_REQUEST:   'Document Request',
    PRICE_ENQUIRY:      'Price Enquiry',
    OTHER:              'Other',
};

// ─── Enquiry detail card ──────────────────────────────────────────────────────
function EnquiryCard({ event }) {
    const p = event.properties_json || {};
    // enquiry_type is stored directly in properties_json
    const rawType   = p.enquiry_type || null;
    const typeLabel = rawType ? (ENQUIRY_TYPE_LABELS[rawType] || rawType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : null;
    // message stored in various possible keys
    const message   = p.message || p.enquiry_message || p.body || p.content || null;
    // submitter contact info (may differ from buyer account)
    const name      = p.name || p.buyer_name || null;
    const email     = p.email || p.buyer_email || null;
    const phone     = p.phone || p.buyer_phone || null;

    if (!message && !typeLabel && !name) return null;

    return (
        <div className="pat-detail-card pat-detail-card--enquiry">
            {typeLabel && (
                <span className="pat-detail-badge pat-detail-badge--type">
                    {typeLabel}
                </span>
            )}
            {message && (
                <p className="pat-detail-message">"{message}"</p>
            )}
            {(name || email || phone) && (
                <div className="pat-detail-contact-line">
                    {name && <span>{name}</span>}
                    {email && <span className="pat-detail-muted">· {email}</span>}
                    {phone && <span className="pat-detail-muted">· {phone}</span>}
                </div>
            )}
        </div>
    );
}


// ─── Offer detail card ────────────────────────────────────────────────────────
function OfferCard({ event }) {
    const p = event.properties_json || {};
    const amount       = p.offer_amount    ? `$${Number(p.offer_amount).toLocaleString('en-AU')}` : null;
    const deposit      = p.deposit_amount  ? `$${Number(p.deposit_amount).toLocaleString('en-AU')}` : null;
    const settlement   = p.settlement_days ? `${p.settlement_days} days` : null;
    const conditions   = Array.isArray(p.conditions) ? p.conditions : [];
    const message      = p.message || null;
    const name         = p.name || null;
    const email        = p.email || null;
    const phone        = p.phone || null;
    const reference    = p.reference || null;

    if (!amount && !name) return null;

    return (
        <div className="pat-detail-card pat-detail-card--offer">
            {/* Offer price prominent */}
            {amount && (
                <div className="pat-offer-amount">
                    <span className="pat-offer-label">Offer Price</span>
                    <span className="pat-offer-price">{amount}</span>
                </div>
            )}
            {/* Secondary details */}
            <div className="pat-offer-meta">
                {deposit    && <span className="pat-offer-meta-item">💰 Deposit: {deposit}</span>}
                {settlement && <span className="pat-offer-meta-item">📅 Settlement: {settlement}</span>}
                {reference  && <span className="pat-offer-meta-item pat-detail-muted">Ref: {reference}</span>}
            </div>
            {/* Conditions */}
            {conditions.length > 0 && (
                <div className="pat-offer-conditions">
                    {conditions.map((c, i) => (
                        <span key={i} className="pat-detail-badge pat-detail-badge--condition">{c}</span>
                    ))}
                </div>
            )}
            {/* Buyer message */}
            {message && (
                <p className="pat-detail-message">"{message}"</p>
            )}
            {/* Contact info */}
            {(name || email || phone) && (
                <div className="pat-detail-contact-line">
                    {name  && <span>{name}</span>}
                    {email && <span className="pat-detail-muted">· {email}</span>}
                    {phone && <span className="pat-detail-muted">· {phone}</span>}
                </div>
            )}
        </div>
    );
}

// ─── Generic detail card (call/email clicks etc.) ────────────────────────────
function IntentCard({ event }) {
    const p = event.properties_json || {};
    const agent = p.agent_name || p.agent_email || null;
    if (!agent) return null;
    return (
        <div className="pat-detail-card">
            <span className="pat-detail-muted">
                {event.event_name === 'call_clicked' ? '📞' : '✉'} Agent: {agent}
            </span>
        </div>
    );
}

// ─── Mortgage calculator detail card ─────────────────────────────────────────
const LOAN_TYPE_LABELS = { PI: 'Principal & interest', IO: 'Interest only' };
const MC_BUYER_TYPE_LABELS = {
    investor: 'Investor',
    first_home_buyer: 'First home buyer',
    owner_occupier: 'Owner-occupier',
};
const FREQ_LABELS = { weekly: 'weekly', fortnightly: 'fortnightly', monthly: 'monthly' };
// Inputs the buyer moved themselves, reported by the portal as fields_changed.
const MC_FIELD_LABELS = {
    loan_amount: 'loan amount',
    property_price: 'property price',
    rate_pct: 'interest rate',
    term_years: 'loan term',
    frequency: 'repayment frequency',
    loan_type: 'loan type',
    io_period: 'interest-only period',
    extra_monthly: 'extra repayments',
    offset: 'offset balance',
    state_code: 'state',
    is_fhb: 'first home buyer',
    is_investment: 'investment property',
    is_new_build: 'new build',
};

const fmtMoney = (val) => {
    if (val === null || val === undefined || val === '' || Number.isNaN(Number(val))) return null;
    return `$${Number(val).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
};
const fmtPct = (val, digits = 2) => {
    if (val === null || val === undefined || val === '' || Number.isNaN(Number(val))) return null;
    return `${Number(val).toFixed(digits)}%`;
};

function MortgageCard({ event }) {
    const p = event.properties_json || {};

    const repayment = fmtMoney(p.repayment);
    const freq      = FREQ_LABELS[p.frequency] || p.frequency || null;
    const loanType  = LOAN_TYPE_LABELS[p.loan_type] || p.loan_type || null;
    const buyerType = MC_BUYER_TYPE_LABELS[p.buyer_type] || null;

    const meta = [
        { label: 'Loan',            value: fmtMoney(p.loan_amount) },
        { label: 'Property price',  value: fmtMoney(p.property_price) },
        { label: 'LVR',             value: fmtPct(p.lvr_pct, 1) },
        { label: 'Rate',            value: fmtPct(p.rate_pct) },
        { label: 'Term',            value: p.term_years ? `${p.term_years} yrs` : null },
        { label: 'Loan type',       value: loanType },
        { label: 'Borrowing power', value: fmtMoney(p.borrowing_power) },
        { label: 'Upfront costs',   value: fmtMoney(p.upfront_costs) },
    ].filter((m) => m.value);

    // Which figures this buyer typed in themselves (rest were pre-filled from
    // the listing). Only present on events logged after Aug 2026.
    const adjusted = Array.isArray(p.fields_changed)
        ? p.fields_changed.map((f) => MC_FIELD_LABELS[f] || f).filter(Boolean)
        : [];

    // Nothing meaningful captured — skip the card entirely.
    if (!repayment && meta.length === 0) return null;

    return (
        <div className="pat-detail-card pat-detail-card--mortgage">
            {repayment && (
                <div className="pat-mortgage-headline">
                    <span className="pat-mortgage-label">Repayment</span>
                    <span className="pat-mortgage-amount">
                        {repayment}
                        {freq && <span className="pat-mortgage-freq"> / {freq}</span>}
                    </span>
                </div>
            )}
            {meta.length > 0 && (
                <div className="pat-mortgage-grid">
                    {meta.map((m) => (
                        <div key={m.label} className="pat-mortgage-item">
                            <span className="pat-mortgage-item-label">{m.label}</span>
                            <span className="pat-mortgage-item-value">{m.value}</span>
                        </div>
                    ))}
                </div>
            )}
            {adjusted.length > 0 && (
                <div className="pat-mortgage-adjusted">
                    Buyer changed: {adjusted.join(', ')}
                </div>
            )}
            {buyerType && (
                <span className="pat-detail-badge pat-detail-badge--mortgage">{buyerType}</span>
            )}
        </div>
    );
}

// ─── Single event item ────────────────────────────────────────────────────────
function EventItem({ event, onBuyerClick }) {
    const cfg = EVENT_CONFIG[event.event_name] || {
        icon: TrendingUp,
        label: event.event_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        color: 'gray',
    };
    const Icon = cfg.icon;

    // Buyer comes as event.buyer = { id, name, email, phone }
    const buyer = event.buyer || null;
    // Anonymous visitors show anonymous ID
    const isAnonymous = !buyer && event.anonymous_id;

    return (
        <div className={`pat-event`}>
            <div className={`pat-event-icon pat-icon--${cfg.color}`}>
                <Icon size={15} />
            </div>
            <div className="pat-event-body">
                <div className="pat-event-header">
                    <span className={`pat-event-label pat-event-label--${cfg.color}`}>{cfg.label}</span>
                    <span className="pat-event-time" title={event.occurred_at}>
                        {formatRelativeTime(event.occurred_at)}
                    </span>
                </div>

                {/* Buyer pill */}
                {buyer && (
                    <BuyerPill buyer={buyer} onBuyerClick={onBuyerClick} />
                )}
                {isAnonymous && (
                    <span className="pat-anonymous-badge">
                        <User size={11} /> Anonymous visitor
                    </span>
                )}

                {/* Enquiry detail */}
                {event.event_name === 'enquiry_sent' && (
                    <EnquiryCard event={event} />
                )}

                {/* Offer detail */}
                {event.event_name === 'offer_submitted' && (
                    <OfferCard event={event} />
                )}

                {/* Intent click detail */}
                {(event.event_name === 'call_clicked' || event.event_name === 'email_clicked') && (
                    <IntentCard event={event} />
                )}

                {/* Mortgage calculator detail — the figures the buyer entered */}
                {event.event_name === 'mortgage_calculator_calculated' && (
                    <MortgageCard event={event} />
                )}

                {/* Inspection request detail */}
                {event.event_name === 'inspection_requested' && event.properties_json?.inspection_time && (
                    <div className="pat-detail-card">
                        <span className="pat-detail-muted">
                            📅 Requested: {formatMelbourneDateTime(event.properties_json.inspection_time)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PropertyAnalyticsTimeline({ propertyId }) {
    const [events, setEvents]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [statDetail, setStatDetail] = useState({ isOpen: false, key: null, eventTypes: null, title: '' });
    // Buyer the agent tapped — opens the quick-actions popup (notes / reminder / email / add to property)
    const [actionsBuyer, setActionsBuyer] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!propertyId) return;
        setLoading(true);
        setError(null);

        // Pass a far-back start date and a high limit so the timeline shows the
        // FULL history of buyer activity for this property — not just the last
        // 30 days (which is the backend default when start_date is omitted).
        const fullHistoryStart = '2000-01-01T00:00:00Z';
        const nowEnd = new Date().toISOString();
        fetchEventDetails(fullHistoryStart, nowEnd, null, propertyId, null, 5000)
            .then(data => {
                // data may be an array or { results: [...] }
                const list = Array.isArray(data) ? data : (data?.results || data?.events || []);
                // Sort newest first
                list.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
                setEvents(list);
            })
            .catch(err => {
                console.error('Error loading property analytics:', err);
                setError('Failed to load analytics data.');
            })
            .finally(() => setLoading(false));
    }, [propertyId]);

    // Tapping a buyer opens an in-place quick-actions popup so the agent can act
    // (note / reminder / email / add to property) without leaving the list.
    const handleBuyerClick = (buyer) => {
        if (!buyer?.id && !buyer?.email) return;
        setActionsBuyer(buyer);
    };

    // "Full profile" inside the popup — resolves the buyer to a CRM contact and navigates.
    const openBuyerProfile = async (buyer) => {
        if (!buyer?.id && !buyer?.email) return;
        const toastId = toast.loading(`Opening ${buyer.name || buyer.email}'s profile…`);
        try {
            const res = await syncVirtualContact(`buyer_${buyer.id}`);
            const contactId = res?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`);
        } catch {
            toast.error('Could not open buyer profile.', { id: toastId });
        }
    };

    const handleStatClick = (key, eventTypes, title) => {
        setStatDetail({ isOpen: true, key, eventTypes, title });
    };

    const closeStatDetail = () => {
        setStatDetail({ isOpen: false, key: null, eventTypes: null, title: '' });
    };

    if (loading) {
        return (
            <div className="pat-state">
                <Loader2 size={28} className="pat-spinner" />
                <p>Loading analytics timeline…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pat-state pat-state--error">
                <AlertCircle size={28} />
                <p>{error}</p>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className="pat-state">
                <TrendingUp size={36} />
                <h4>No Buyer Portal Activity</h4>
                <p>No buyers have interacted with this property through the Buyer Portal yet.</p>
            </div>
        );
    }

    const PREVIEW_COUNT = 20;
    const visibleEvents = showAll ? events : events.slice(0, PREVIEW_COUNT);
    const hasMore = events.length > PREVIEW_COUNT;

    return (
        <div className="pat-root">
            <StatsBar events={events} onStatClick={handleStatClick} />

            <div className="pat-timeline">
                {visibleEvents.map((event, idx) => (
                    <EventItem
                        key={event.event_id || idx}
                        event={event}
                        onBuyerClick={handleBuyerClick}
                    />
                ))}
            </div>

            {hasMore && (
                <button className="pat-show-more" onClick={() => setShowAll(prev => !prev)}>
                    {showAll
                        ? <><ChevronUp size={15} /> Show less</>
                        : <><ChevronDown size={15} /> Show all {events.length} events</>
                    }
                </button>
            )}

            <StatDetailModal
                isOpen={statDetail.isOpen}
                onClose={closeStatDetail}
                title={statDetail.title}
                events={events}
                eventTypes={statDetail.eventTypes}
                onBuyerClick={handleBuyerClick}
            />

            {actionsBuyer && (
                <BuyerActionsModal
                    buyer={actionsBuyer}
                    propertyId={propertyId}
                    onClose={() => setActionsBuyer(null)}
                    onViewProfile={openBuyerProfile}
                />
            )}
        </div>
    );
}
